/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / DISCOUNTS-ADMIN — Rabatt-Verwaltung (S179 / Teil B · R2)
   =============================================================================
   Cockpit-Subtab „Rabatte" unter „System" (neben Deals). Verwaltet die
   prozentualen Rabatte (phase_131, Tabelle public.discounts) — z. B. „20 %
   Friends & Family", „40 % Mitarbeiter". NUR prozentual (Spec §6a), KEIN
   Verfügbarkeitsfenster (anders als Deals — Rabatte sind immer wählbar). Das
   Anwenden beim Kassieren ist R3.

   ARCHITEKTUR (1:1 wie Deals-Tab, Jonas-Entscheid §4/§6):
   - Verwaltungs-Liste liest DIREKT per RLS-SELECT (supa.from('discounts'),
     inkl. inaktiver, exkl. soft-deleted) — NICHT discounts_for_pos() (das ist
     nur die schlanke POS-Liste für R3).
   - Schreiben DIREKT via window.App.supabase.from('discounts') insert/update
     (RLS tenant + owner/admin/manager) — KEINE Schreib-RPCs. IDs via uid('disc').
     Löschen = Soft-Delete (deleted_at = now()).
   - Non-destruktive Renders: Skalar-Felder (Name/Prozent/Sortierung) per
     onchange → DB-Write OHNE Tab-Re-Render (Fokus bleibt). NUR Struktur-Klicks
     (anlegen/löschen) rendern neu; Aktiv-Toggle patcht nur lokal.
   - Inline-Handler als func() (CSP/dom-bindings, Memory gg-inline-handler-csp).

   Rechte: owner/admin/manager (auth.canEdit('discounts'), PERMISSIONS in
   public/adapters/auth.js). service/staff/lager sehen die System-Gruppe nicht.
============================================================================ */

import { htmlEscape, uid, showToast, requireEditPermission } from '../../utils/helpers.js';

const _state = {
  discounts: [],   // direkter RLS-SELECT (inkl. inaktiver)
  loading: false
};

/* -------------------------------------------------------------------------- */
/* Infrastruktur                                                               */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }
function _rid() {
  const a = window.App && window.App.auth;
  return (a && typeof a.getCurrentRestaurantIdSync === 'function' && a.getCurrentRestaurantIdSync())
    || (window.AppConfig && window.AppConfig.defaultRestaurantId) || null;
}

/** Prozent-Eingabe defensiv parsen + auf 0<x<=100 prüfen. null = ungültig. */
function _parsePercent(v) {
  const n = parseFloat(String(v == null ? '' : v).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0 || n > 100) return null;
  // Auf 2 Nachkommastellen normalisieren (z. B. 33.333 → 33.33).
  return Math.round(n * 100) / 100;
}

function _fmtPercent(p) {
  const n = Number(p) || 0;
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
}

/* -------------------------------------------------------------------------- */
/* Laden + Render                                                              */
/* -------------------------------------------------------------------------- */

/** Entry: lädt Rabatte (direkter RLS-SELECT) und rendert die Liste in #tab-discounts. */
export async function renderDiscountsAdmin() {
  const root = document.getElementById('tab-discounts');
  if (!root) return;

  root.innerHTML = `<div class="settings-card"><p class="hint">Lade Rabatte…</p></div>`;
  const supa = _supa();
  if (!supa) { root.innerHTML = `<div class="settings-card"><p class="hint">Keine Verbindung.</p></div>`; return; }
  try {
    const { data, error } = await supa
      .from('discounts')
      .select('id, name, percent, active, sort_order')
      .is('deleted_at', null)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (error) throw error;
    _state.discounts = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('[discounts] list:', e);
    root.innerHTML = `<div class="settings-card"><p class="hint">Rabatte konnten nicht geladen werden: ${htmlEscape(e.message || String(e))}</p></div>`;
    return;
  }
  _renderList(root);
}

function _rowHtml(d) {
  return `
    <tr data-discount="${htmlEscape(d.id)}">
      <td>
        <label class="deal-active-toggle" title="Aktiv">
          <input type="checkbox" ${d.active ? 'checked' : ''} onchange="discountsToggleActive('${htmlEscape(d.id)}', this.checked)">
        </label>
      </td>
      <td>
        <input type="text" class="discount-name-input" value="${htmlEscape(d.name || '')}" placeholder="Rabatt-Name (z. B. Friends & Family)"
               onfocus="this.select()" onchange="discountsUpdate('${htmlEscape(d.id)}','name', this.value)">
      </td>
      <td class="num">
        <input type="number" class="discount-percent-input" step="0.01" min="0.01" max="100" value="${_fmtPercent(d.percent)}"
               onchange="discountsUpdate('${htmlEscape(d.id)}','percent', this.value)" style="width:6em;"> %
      </td>
      <td class="num">
        <input type="number" class="discount-sort-input" step="1" min="0" value="${Number(d.sort_order) || 0}"
               onchange="discountsUpdate('${htmlEscape(d.id)}','sort_order', this.value)" style="width:5em;">
      </td>
      <td class="deal-row-actions">
        <button class="btn btn-ghost btn-sm" onclick="discountsDelete('${htmlEscape(d.id)}')" title="Löschen">✕</button>
      </td>
    </tr>`;
}

function _renderList(root) {
  const rows = (_state.discounts || []).map(_rowHtml).join('');
  root.innerHTML = `
    <div class="settings-card">
      <div class="settings-card-head">
        <div>
          <h3 style="margin:0;">Rabatte</h3>
          <p class="hint" style="margin:.2rem 0 0;">Prozentuale Rabatte (z. B. Friends &amp; Family, Mitarbeiter). Werden beim Kassieren wählbar und GoBD-korrekt vor der TSE-Signatur eingerechnet (Netto je Steuersatz). Jeder Operator darf einen aktiven Rabatt anwenden.</p>
        </div>
        <button class="btn btn-primary" onclick="discountsCreate()">+ Rabatt anlegen</button>
      </div>
      ${(_state.discounts || []).length === 0
        ? `<p class="hint" style="margin-top:14px;">Noch keine Rabatte angelegt.</p>`
        : `<table class="deals-table"><thead><tr><th>Aktiv</th><th>Name</th><th>Prozent</th><th>Sortierung</th><th></th></tr></thead><tbody>${rows}</tbody></table>`}
    </div>
  `;
}

/* -------------------------------------------------------------------------- */
/* Handler                                                                     */
/* -------------------------------------------------------------------------- */

export async function discountsCreate() {
  if (!requireEditPermission('discounts', 'Rabatt anlegen')) return;
  const supa = _supa(); const rid = _rid();
  if (!supa || !rid) { showToast('Keine Verbindung.', 'error'); return; }
  try {
    const { error } = await supa.from('discounts').insert({
      id: uid('disc'), restaurant_id: rid, name: 'Neuer Rabatt', percent: 10,
      active: false, sort_order: (_state.discounts.length + 1) * 10
    });
    if (error) throw error;
  } catch (e) { console.error('[discounts] create:', e); showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  await renderDiscountsAdmin();
}

/** Skalar-Patch (Name/Prozent/Sortierung) ohne Re-Render — Fokus bleibt. */
export async function discountsUpdate(id, key, val) {
  if (!requireEditPermission('discounts', 'Rabatt ändern')) return;
  const supa = _supa(); if (!supa) return;
  const patch = {};
  if (key === 'percent') {
    const p = _parsePercent(val);
    if (p == null) {
      showToast('Prozent muss zwischen 0 und 100 liegen.', 'error');
      // Feld auf gespeicherten Wert zurücksetzen.
      const d0 = _state.discounts.find(x => x.id === id);
      try {
        const inp = document.querySelector(`tr[data-discount="${id}"] .discount-percent-input`);
        if (inp && d0) inp.value = _fmtPercent(d0.percent);
      } catch (_) { /* tolerant */ }
      return;
    }
    patch.percent = p;
  } else if (key === 'sort_order') {
    patch.sort_order = Math.max(0, Math.round(Number(val) || 0));
  } else if (key === 'name') {
    patch.name = String(val == null ? '' : val).trim();
    if (!patch.name) { showToast('Name darf nicht leer sein.', 'error'); return; }
  } else {
    return;
  }
  patch.updated_at = new Date().toISOString();
  // Optimistisch vor dem await (Race-Schutz wie deals _patchDeal).
  const d = _state.discounts.find(x => x.id === id); if (d) Object.assign(d, patch);
  try {
    const { error } = await supa.from('discounts').update(patch).eq('id', id);
    if (error) throw error;
  } catch (e) { console.error('[discounts] update:', e); showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function discountsToggleActive(id, checked) {
  if (!requireEditPermission('discounts', 'Rabatt aktivieren')) { renderDiscountsAdmin(); return; }
  const supa = _supa(); if (!supa) return;
  try {
    const { error } = await supa.from('discounts').update({ active: !!checked, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    const d = _state.discounts.find(x => x.id === id); if (d) d.active = !!checked;
  } catch (e) { console.error('[discounts] toggle:', e); showToast('Konnte nicht umgeschaltet werden.', 'error'); renderDiscountsAdmin(); }
}

export async function discountsDelete(id) {
  if (!requireEditPermission('discounts', 'Rabatt löschen')) return;
  const d = _state.discounts.find(x => x.id === id);
  if (!(await window.styledConfirm({ title: `Rabatt „${(d && d.name) || id}" löschen?`, danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  const supa = _supa(); if (!supa) return;
  try {
    // Soft-Delete (GoBD: historische Bons referenzieren den Snapshot, nicht diese Zeile).
    const { error } = await supa.from('discounts')
      .update({ deleted_at: new Date().toISOString(), active: false, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  } catch (e) { console.error('[discounts] delete:', e); showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  showToast('Rabatt gelöscht.', 'info');
  renderDiscountsAdmin();
}

/* Test-Hook */
export function _resetForTest() { _state.discounts = []; _state.loading = false; }

/* -------------------------------------------------------------------------- */
/* window-Bridge (CSP: inline func())                                          */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDiscountsAdmin,
    discountsCreate, discountsUpdate, discountsToggleActive, discountsDelete
  });
}
